import { formatUtcFetchTimestamp } from '@/utils/dateutil';
import React from 'react';
import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import { useSelector } from 'react-redux';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { FormControlLabel, Checkbox, Drawer, styled, TextField, Button as MUIButton, IconButton } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { css } from '@emotion/react';
import { currentNursingHomeSelector } from '../../../../redux/config/currentNursingHome';
import { showMedicalContentSelector } from '../../../../redux/config/selfData';
import { v4 as uuid } from 'uuid';
import api from '../../../../api';
import { color } from '../../../../styles';
import { useLoading } from '../../../../contexts/LoadingContext';
import { useSnackbar } from '../../../../contexts/SnackbarContext';
import { calcAxisMax } from '@/utils/dataFormatUtil';
import { Button } from '../../../../components/mui/button';
import { OutlineButton } from '../../../../components/outlineButton';
import { RecordDialog } from '../../../../components/careRecord/recordDialog';
import { IGetUltraSonicDataSetsRes } from '../../../../types/api/ultra-sonic-data/getUltraSonicDataSets';
import { IUpdateResidentParams } from '../../../../types/api/resident/updateResident';
import { IGetNotificationRes } from '../../../../types/api/notification/getNotification';
import { IGetCareRecordRes } from '../../../../types/api/care-record/getCareRecord';
import { IGetResidentRes } from '../../../../types/api/resident/getResident';
import { IGetSensorReaction, IGetSensorReactionRes } from '../../../../types/api/ultra-sonic-data/getSensorReaction';
import { IGetResamples, IResample } from '../../../../types/api/ultra-sonic-data/getResample';
import { IGetActivityStatusAnalysisRes } from '../../../../types/api/ultra-sonic-data/getActivityStatusAnalysis';
import { IGetVisualizedUrineVolume, IGetVisualizedUrineVolumeRes } from '../../../../types/api/medical/getVisualizedUrineVolume';
import { DetailGraphItem } from './detailGraphItem';
import { ChangeLineModal } from './changeLine';
import { Dataset } from './dataset';
import { ActivityStatusGraphCaption } from '../urineTrendAnalysis/caption';
import { createActivityStatusHeatmapBar } from '@/utils/activityStatusUtils';
import { RawGraphHintTooltip } from '../../dfreeGraphList/rawGraphHintTooltip';
import { useServiceTranslation } from '@/hooks/useServiceTranslation';

interface Props {
  residentId: string;
}

interface ScatterItem {
  x: number;
  y: number;
  color: string;
  name: string;
}

interface ResidualUrineItem {
  x: number;
  ruv: number | null;
  accumulation: number | null;
}

const cloneSeriesData = (data: [number, number | null][]) => data.map(([x, y]) => [x, y] as [number, number | null]);
const alignScatterY = (data: ScatterItem[], y: number) => data.map((item) => ({ ...item, y }));

const GraphContainer = css`
  padding: 8px 24px 24px;
  margin: 12px auto;
  z-index: 0;
`;

const GraphHeader = css`
  position: sticky;
  z-index: 1;
  top: 0;
  border-bottom: 1px solid ${color.border_gray};
  padding: 24px 24px 8px;
  background: ${color.white};
`;

const ButtonContainer = css`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const ButtonRightElement = css`
  display: flex;
  align-items: center;
`;

const LineChangeButton = styled(Button)`
  border-radius: 60px;
  padding: 8px 32px;
  font-weight: bold;
  font-size: 14px;
  background: ${color.notification_yellow};

  &:hover {
    background: ${color.notification_yellow};
  }
`;

const DatePickerContainer = css`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 16px;
`;

const CareRecordLabelRow = css`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const RawGraphLabel = css`
  display: inline-flex;
  align-items: center;
  gap: 0 2px;
`;

const CareRecordLabel = css`
  display: flex;
  align-items: center;
  font-size: 12px;
  line-height: 100%;
  margin-right: 32px;

  color: ${color.dark_gray};
  & > span {
    margin-left: 8px;
  }
`;

const ColorRect = css`
  border: 1px solid ${color.border_gray};
  width: 12px;
  height: 12px;
`;

const ColorLine = css`
  background: ${color.notification_yellow};
  width: 30px;
  height: 3px;
`;

const ColorCircle = css`
  width: 12px;
  height: 12px;
  border-radius: 6px;
`;

const ColorDiamond = css`
  width: 12px;
  height: 12px;
  background: #2c8ed9;
  transform: rotate(45deg) scale(0.8);
`;

const CaptionTitle = css`
  line-height: 1.8em;
  font-size: 24px;
  font-weight: bold;
`;

const CaptionContainer = css`
  display: grid;
  margin-bottom: 16px;
`;

const CaptionText = css`
  line-height: 1.8em;
  font-size: 18px;
  font-weight: bold;
  color: ${color.green};
`;

const AnnotationText = css`
  line-height: 1.6em;
  font-size: 14px;
`;

export const ResidentDetailGraph: React.FC<Props> = ({ residentId }) => {
  const { showSnackbar } = useSnackbar();
  const { showLoading, hideLoading } = useLoading();
  const { t, serviceT } = useServiceTranslation();
  const currentNursingHome = useSelector(currentNursingHomeSelector);
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const [range, setRange] = React.useState<{ start: dayjs.Dayjs; end: dayjs.Dayjs; isCreate: boolean }>({
    start: dayjs().local().set('hour', 0).set('minute', 0).set('second', 0).utc(),
    end: dayjs().local().add(1, 'day').set('hour', 0).set('minute', 0).set('second', 0).utc(),
    isCreate: false,
  });
  const [charts, setCharts] = React.useState<
    {
      id: string;
      option: Highcharts.Options;
      heatmap: Highcharts.Options;
      range: { start: dayjs.Dayjs; end: dayjs.Dayjs };
      urineValueData?: [number, number | null][];
      urineRawValueData?: [number, number | null][];
      urineVolumeData?: [number, number | null][];
      urineRawVolumeData?: [number, number | null][];
      residualUrineData?: ResidualUrineItem[];
    }[]
  >([]);
  const [isDrawerOpen, setDrawerOpen] = React.useState(false);
  const [isLineChangeModalOpen, setLineChangeModalOpen] = React.useState(false);
  const [isUrineVolumeCaptionOpen, setUrineVolumeCaptionOpen] = React.useState(false);
  const [isActivityStatusViewMode, setActivityStatusViewMode] = React.useState(false);
  const [isAbsoluteViewMode, setAbsoluteViewMode] = React.useState<boolean>(showMedicalContent);
  const [isResampleView, setResampleView] = React.useState(false);
  const [datasets, setDataset] = React.useState<IGetUltraSonicDataSetsRes>();
  const [resident, setResident] = React.useState<IGetResidentRes>();
  const hasSetMedicalAbsoluteDefaultRef = React.useRef(false);

  React.useEffect(() => {
    if (!showMedicalContent) return;
    if (hasSetMedicalAbsoluteDefaultRef.current) return;
    hasSetMedicalAbsoluteDefaultRef.current = true;
    setAbsoluteViewMode(true);
  }, [showMedicalContent]);

  const fetchDataset = React.useCallback(async () => {
    if (!range || showMedicalContent) return;
    showLoading();
    try {
      const [datasets, favorites] = await Promise.all([
        api.get('/residents/:residentId/ultra-sonic-data-sets', {
          params: { residentId, sort: 'desc', limit: 5000 },
        }),
        api.get('/residents/:residentId/favorites/data-set', {
          params: { residentId },
        }),
      ]);

      // データセットドロワー部分で表示したいものだけにする
      const datasetsWithFavorite = (datasets || []).map((v) => {
        if (!favorites) return { ...v, isFavorite: false };
        const favoriteDate = favorites.find((favorite) => favorite.from === v.begin);
        if (!favoriteDate) return { ...v, isFavorite: false };
        return { ...v, isFavorite: true, favoriteId: favoriteDate.id };
      });
      setDataset(datasetsWithFavorite);
    } finally {
      hideLoading();
    }
  }, [hideLoading, range, residentId, showLoading, showMedicalContent]);

  const handleUpdateFavorite = React.useCallback(
    async (start: string, end: string, favoriteId?: number) => {
      if (!datasets) return;
      const favoriteIndex = datasets.findIndex((v) => v.begin === start);
      if (favoriteIndex === -1) return;
      if (!favoriteId) {
        const res = await api.post('/residents/:residentId/favorites/data-set', { residentId, from: start, to: end });
        setDataset((prev) => {
          if (!prev) return [];
          prev[favoriteIndex].favoriteId = res.id;
          prev[favoriteIndex].isFavorite = true;
          return [...prev];
        });
      } else {
        await api.delete('/residents/:residentId/favorites/data-set/:favoriteDatasetId', {
          params: { residentId, favoriteDatasetId: favoriteId },
        });
        setDataset((prev) => {
          if (!prev) return [];
          prev[favoriteIndex].favoriteId = undefined;
          prev[favoriteIndex].isFavorite = false;
          return [...prev];
        });
      }
    },
    [datasets]
  );

  const handleCreateChart = React.useCallback((start: string, end: string) => {
    setRange({ start: dayjs(start).local().utc(), end: dayjs(end).local().utc(), isCreate: true });
  }, []);

  const handleUpdateChart = React.useCallback((start: string, end: string) => {
    setRange({ start: dayjs(start).local().utc(), end: dayjs(end).local().utc(), isCreate: false });
    handleCloseDrawer();
  }, []);

  const handleOpenLineChangeModal = React.useCallback(() => {
    setLineChangeModalOpen(true);
  }, []);

  const handleCloseLineChangeModal = React.useCallback(() => {
    setLineChangeModalOpen(false);
  }, []);

  const handleOpenDrawer = React.useCallback(() => {
    if (datasets && datasets.length > 0) {
      setDrawerOpen(true);
    } else {
      showSnackbar(t('noDataset', 'データセットがありません'));
    }
  }, [datasets]);

  const handleCloseDrawer = React.useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleDeleteGraph = React.useCallback(
    (id: string) => {
      setCharts((prev) => {
        return prev.filter((v) => v.id !== id);
      });
    },
    [charts]
  );

  const yAxisLabelFormatter = React.useCallback(
    (value: any, isAbsoluteMode: boolean, isMedical: boolean) => {
      if (!isAbsoluteMode) return value.toString();
      return isMedical ? `${value}ml` : `${+value * 40}ml`;
    },
    [charts, isAbsoluteViewMode]
  );

  const tooltipFormatter = React.useCallback(
    (x: any, y: any, isAbsoluteMode: boolean, isMedical: boolean) => {
      const dateFormat = t('dayjsGraphDateTimeFormat', 'MM月DD日 HH時mm分');
      const localTime = dayjs(x).format(dateFormat);
      const yValue =
        y !== undefined
          ? isAbsoluteMode
            ? isMedical
              ? `${y}ml`
              : `${y * 40}ml`
            : y
          : '';
      return `${yValue}<br />${localTime}`;
    },
    [charts, isAbsoluteViewMode]
  );

  const createResidualUrinePlotData = React.useCallback(
    (data: ResidualUrineItem[], isAbsoluteMode: boolean): ScatterItem[] => {
      const label = isAbsoluteMode
        ? t('residualUrineVolume', '残尿量')
        : t('UrineAccumulation', '尿のたまり具合');
      const plotData: ScatterItem[] = [];

      data.forEach((record) => {
        const value = isAbsoluteMode ? record.ruv : record.accumulation;
        if (typeof value !== 'number' || !Number.isFinite(value)) return;

        plotData.push({
          x: record.x,
          y: value,
          color: '#2C8ED9',
          name: `${label}: ${value}${isAbsoluteMode ? 'ml' : ''}`,
        });
      });

      return plotData;
    },
    [t],
  );

  const createXAxisPlotLines = React.useCallback(
    (start: dayjs.Dayjs, end: dayjs.Dayjs, residualUrineData: ScatterItem[]): Highcharts.XAxisPlotLinesOptions[] => [
      {
        color: '#CCCCCC',
        dashStyle: 'Solid',
        value: Date.parse(start.format()),
        width: 0.5,
      },
      {
        color: '#CCCCCC',
        dashStyle: 'Solid',
        value: Date.parse(end.format()),
        width: 0.5,
      },
      ...residualUrineData.map((record) => ({
        color: '#2C8ED9',
        dashStyle: 'Solid' as const,
        value: record.x,
        width: 1,
        zIndex: 2,
      })),
    ],
    [],
  );

  const getMedicalAbsoluteYAxisMax = React.useCallback(
    (
      volumeData: [number, number | null][],
      rawVolumeData: [number, number | null][],
      residualUrineData: ResidualUrineItem[] = [],
    ) => {
      const values = [...volumeData, ...rawVolumeData]
        .map(([, y]) => y)
        .filter((y): y is number => typeof y === 'number' && Number.isFinite(y));
      residualUrineData.forEach((record) => {
        if (typeof record.ruv === 'number' && Number.isFinite(record.ruv)) values.push(record.ruv);
      });
      const max = values.length ? Math.max(...values) : 0;
      return calcAxisMax(max);
    },
    [calcAxisMax]
  );

  const getYGrids = React.useCallback((bigBladderNotification = false, threshold = 0) => {
    let grids: Highcharts.YAxisPlotLinesOptions[] = [];

    grids = [
      ...grids,
      {
        color: bigBladderNotification ? '#FFBB00' : '#C0C0C0',
        dashStyle: 'Solid',
        value: threshold / 10 - 0.03,
        width: 2,
        zIndex: -3,
      },
    ];
    return grids;
  }, []);

  const toggleActivityStatus = React.useCallback((isChecked: boolean) => {
    setActivityStatusViewMode(isChecked);
  }, []);

  const updateChartForAbsoluteMode = React.useCallback(
    (chart: (typeof charts)[number], isChecked: boolean) => {
      // y軸目盛を絶対値↔︎相対値に切り替える + medical 実測値表示のときは max を調整
      const yAxisMax =
        showMedicalContent && isChecked && chart.urineVolumeData && chart.urineRawVolumeData
          ? getMedicalAbsoluteYAxisMax(chart.urineVolumeData, chart.urineRawVolumeData, chart.residualUrineData)
          : 10;
      const tickInterval =
        showMedicalContent && isChecked && chart.urineVolumeData && chart.urineRawVolumeData ? yAxisMax / 5 : 2;
      const residualUrinePlotData =
        showMedicalContent && chart.residualUrineData
          ? createResidualUrinePlotData(chart.residualUrineData, isChecked)
          : [];

      const series = (chart.option.series || []) as any[];
      if (!series[0] || !series[1]) return chart;

      const nextOption: Highcharts.Options = { ...chart.option };
      const nextSeries = [...series];
      if (nextOption.xAxis && !Array.isArray(nextOption.xAxis)) {
        nextOption.xAxis = {
          ...nextOption.xAxis,
          plotLines: createXAxisPlotLines(chart.range.start, chart.range.end, residualUrinePlotData),
        };
      }

      const nextSeries0 = { ...nextSeries[0] };
      const nextSeries1 = { ...nextSeries[1] };

      // tooltip.pointformatを絶対値↔︎相対値に切り替える
      if (nextSeries0.tooltip) {
        nextSeries0.tooltip = { ...nextSeries0.tooltip };
        nextSeries0.tooltip.pointFormatter = function () {
          return tooltipFormatter(this.x, this.y, isChecked, showMedicalContent);
        };
      }
      if (nextSeries1.tooltip) {
        nextSeries1.tooltip = { ...nextSeries1.tooltip };
        nextSeries1.tooltip.pointFormatter = function () {
          return tooltipFormatter(this.x, this.y, isChecked, showMedicalContent);
        };
      }

      // medical の場合は series の元データ自体を切り替える（value/rawValue ↔ volume/rawVolume）
      if (
        showMedicalContent &&
        chart.urineValueData &&
        chart.urineRawValueData &&
        chart.urineVolumeData &&
        chart.urineRawVolumeData
      ) {
        nextSeries0.data = cloneSeriesData(isChecked ? chart.urineVolumeData : chart.urineValueData);
        nextSeries1.data = cloneSeriesData(isChecked ? chart.urineRawVolumeData : chart.urineRawValueData);
      }
      if (nextSeries[3]?.data) {
        nextSeries[3] = { ...nextSeries[3], data: alignScatterY(nextSeries[3].data as ScatterItem[], yAxisMax) };
      }
      if (showMedicalContent && chart.residualUrineData) {
        const residualUrineSeriesName = isChecked
          ? t('residualUrineVolume', '残尿量')
          : t('UrineAccumulation', '尿のたまり具合');
        if (nextSeries[4]) {
          nextSeries[4] = { ...nextSeries[4], name: residualUrineSeriesName, data: residualUrinePlotData };
        }
      }

      nextSeries[0] = nextSeries0;
      nextSeries[1] = nextSeries1;
      nextOption.series = nextSeries as any;



      if (Array.isArray(nextOption.yAxis)) {
        const y0 = nextOption.yAxis[0] ? { ...(nextOption.yAxis[0] as any) } : {};
        y0.labels = { ...(y0.labels || {}) };
        y0.labels.formatter = function () {
          return yAxisLabelFormatter(this.value, isChecked, showMedicalContent);
        };
        y0.max = yAxisMax;
        y0.min = 0;
        y0.tickInterval = tickInterval;
        nextOption.yAxis = [y0, ...(nextOption.yAxis.slice(1) as any)];
      } else if (nextOption.yAxis) {
        const y0 = { ...(nextOption.yAxis as any) };
        y0.labels = { ...(y0.labels || {}) };
        y0.labels.formatter = function () {
          return yAxisLabelFormatter(this.value, isChecked, showMedicalContent);
        };
        y0.max = yAxisMax;
        y0.min = 0;
        y0.tickInterval = tickInterval;
        nextOption.yAxis = y0;
      }

      // 姿勢mapの余白を変える
      const nextHeatmap: Highcharts.Options = chart.heatmap ? { ...chart.heatmap } : chart.heatmap;
      if (nextHeatmap?.chart) {
        nextHeatmap.chart = { ...(nextHeatmap.chart as any), marginLeft: isChecked ? 63 : 40 };
      }

      return { ...chart, option: nextOption, heatmap: nextHeatmap };
    },
    [
      createResidualUrinePlotData,
      createXAxisPlotLines,
      getMedicalAbsoluteYAxisMax,
      showMedicalContent,
      t,
      tooltipFormatter,
      yAxisLabelFormatter,
    ]
  );

  const toggleAbsoluteSensorReaction = React.useCallback(
    (isChecked: boolean) => {
      setAbsoluteViewMode(isChecked);
      setCharts((prev) => (prev || []).map((chart) => updateChartForAbsoluteMode(chart, isChecked)));
    },
    [updateChartForAbsoluteMode]
  );

  const toggleResample = React.useCallback(
    (isChecked: boolean) => {
      setCharts((prev) => {
        return (prev || []).map((chart) => {
          const series = (chart.option.series || []) as any[];
          if (!series[1]) return chart;

          const nextOption: Highcharts.Options = { ...chart.option };
          const nextSeries = [...series];
          const nextSeries1 = { ...nextSeries[1], visible: isChecked };
          nextSeries[1] = nextSeries1;
          nextOption.series = nextSeries as any;

          return { ...chart, option: nextOption };
        });
      });
    },
    []
  );

  const createSensorReactionData = React.useCallback(
    (data: IGetSensorReactionRes) => {
      const destSensorReactions: [number, number | null][] = [];
      let prevTimestamp: number;

      data.forEach((v: IGetSensorReaction) => {
        // 前後から25分以上離れていたら切断
        const timestamp = Date.parse(v.timestamp);
        if (timestamp - prevTimestamp > 60 * 1000 * 25) {
          destSensorReactions.push([timestamp - 60 * 1000, null]);
        }
        prevTimestamp = timestamp;
        destSensorReactions.push([timestamp, v.u1xActiveRatio * 10]);
      });

      return destSensorReactions;
    },
    [residentId, range, resident, isResampleView, isAbsoluteViewMode, isActivityStatusViewMode]
  );

  const createResampleData = React.useCallback(
    (data: IGetResamples) => {
      const destResamples: [number, number | null][] = [];
      let prevTimestamp: number;

      data.map((v: IResample) => {
        // 前後から25分以上離れていたら切断
        const timestamp = Date.parse(v.timestamp);
        if (timestamp - prevTimestamp > 60 * 1000 * 25) {
          destResamples.push([timestamp - 60 * 1000, null]);
        }
        prevTimestamp = timestamp;
        destResamples.push([timestamp, v.semiSensorSummary * 10]);
      });

      return destResamples;
    },
    [residentId, range, resident, isResampleView, isAbsoluteViewMode, isActivityStatusViewMode]
  );

  const createVisualizedUrineVolumeData = React.useCallback(
    (data: IGetVisualizedUrineVolumeRes, key: 'value' | 'rawValue' | 'volume' | 'rawVolume'): [number, number | null][] => {
      const dest: [number, number | null][] = [];
      let prevTimestamp: number;

      (data || []).forEach((v: IGetVisualizedUrineVolume) => {
        const timestamp = Date.parse(v.timestamp);
        // 前後から25分以上離れていたら切断
        if (timestamp - prevTimestamp > 60 * 1000 * 25) {
          dest.push([timestamp - 60 * 1000, null]);
        }
        prevTimestamp = timestamp;
        const raw = v[key];
        // 補正後のvalueは10で割って整数に四捨五入、補正前のrawValueは10で割る
        const normalized =
          key === 'value'
            ? Math.round((raw || 0) / 10)
            : key === 'rawValue'
              ? (raw || 0) / 10
              : raw;
        dest.push([timestamp, normalized]);
      });

      return dest;
    },
    [residentId, range, resident, isResampleView, isAbsoluteViewMode, isActivityStatusViewMode]
  );

  const createNotificationScatter = React.useCallback(
    (data: IGetNotificationRes[]) => {
      const scatterData = data
        .filter((notification) => {
          const { notificationType } = notification;
          return notificationType === 'urinated' || notificationType === 'bigBladder' || notificationType === 'gotUp';
        })
        .map((notification) => {
          const { notificationType, timestamp } = notification;
          let label = '';
          let color = '';
          if (notificationType === 'urinated') {
            label = t('checkGraphLabel', 'でたかも');
            color = 'rgba(255, 143, 165, 0.8)';
          } else if (notificationType === 'bigBladder') {
            label = t('almostGraphLabel', 'そろそろ');
            color = 'rgba(255, 187, 0, 0.8)';
          } else {
            label = serviceT('gotUpGraphLabel');
            color = 'rgba(255, 50, 35, 0.8)';
          }

          return {
            x: Date.parse(timestamp),
            y: 0,
            name: label,
            color: color,
          };
        });
      return scatterData.length ? scatterData : [];
    },
    [residentId, range, resident, isResampleView, isAbsoluteViewMode, isActivityStatusViewMode, serviceT, t]
  );

  const createCareRecordScatter = React.useCallback(
    (data: IGetCareRecordRes[]) => {
      const careRecordPlots = data
        .filter((v) => v.urinatedToilet !== null && v.padUrineVolumeType !== null)
        .map((v) => ({
          timestamp: v.timestamp,
          toilet: v.urinatedToilet,
          pad: v.padUrineVolumeType !== 'none' && !!v.padUrineVolumeType,
        }));

      const scatterData = (careRecordPlots || []).map((data) => {
        const { toilet, pad, timestamp } = data;
        const label = `${t('toilet', 'トイレ')}: ${toilet ? t('yes', '有') : t('no', '無')} ${t(
          'diaper',
          'おむつ/パッド'
        )}: ${pad ? t('yes', '有') : t('no', '無')}`;
        let color = '';
        if (toilet && !pad) color = '#72fbfd';
        if (toilet && pad) color = '#75f94c';
        if (!toilet && pad) color = '#CCCCCC';
        if (!toilet && !pad) color = '#6D6D6D';
        return {
          x: Date.parse(timestamp),
          y: 10,
          name: label,
          color,
        };
      });

      return scatterData.length ? scatterData : [];
    },
    [residentId, range, resident, isResampleView, isAbsoluteViewMode, isActivityStatusViewMode]
  );

  const createResidualUrineData = React.useCallback(
    (data: IGetCareRecordRes[]): ResidualUrineItem[] => {
      if (!showMedicalContent) return [];

      return data
        .map((record) => ({
          x: Date.parse(record.timestamp),
          ruv: typeof record.ruv === 'number' && Number.isFinite(record.ruv) ? record.ruv : null,
          accumulation:
            typeof record.accumulation === 'number' && Number.isFinite(record.accumulation)
              ? record.accumulation
              : null,
        }))
        .filter((record) => record.ruv !== null || record.accumulation !== null);
    },
    [showMedicalContent],
  );

  const createActivityStatusData = React.useCallback(
    (data: IGetActivityStatusAnalysisRes) => {
      return createActivityStatusHeatmapBar(data);
    },
    [residentId, range, resident]
  );

  const fetchCharts = React.useCallback(() => {
    const params = { residentId, from: formatUtcFetchTimestamp(range.start), to: formatUtcFetchTimestamp(range.end) };
    const medicalParams = { residentId, from: formatUtcFetchTimestamp(range.start), to: formatUtcFetchTimestamp(range.end), limit: 100000 };

    const urinePromise = showMedicalContent
      ? api.get('/md/residents/:residentId/visualized-urine-volume', { params:medicalParams })
      : api.get('/residents/:residentId/sensor-reactions', { params });

    const rawUrinePromise = showMedicalContent
      ? Promise.resolve([])
      : api.get('/traversal/residents/:residentId/sensor-reaction-resamples', { params });

    const activityStatusPromise = showMedicalContent
      ? api.get('/md/residents/:residentId/activity-status-analysis', { params: medicalParams })
      : api.get('/residents/:residentId/activity-status-analysis', { params });

    Promise.all([
      api.get('/residents/:residentId', { params: { residentId } }),
      urinePromise,
      api.get('/residents/:residentId/notifications', { params: { ...params, limit: 10000 } }),
      rawUrinePromise,
      api.get('/residents/:residentId/care-records', { params: { ...params, limit: 10000 } }),
      activityStatusPromise,
    ]).then(([resident, urineDataRes, notifications, rawUrineRes, records, activityStatuses]) => {
      const notificationScatter = createNotificationScatter(notifications);
      const careRecordScatter = createCareRecordScatter(records);
      const residualUrineData = createResidualUrineData(records);
      const activityStatusData = createActivityStatusData(activityStatuses);

      // グラフの元データ（10段階）を用意
      let urineValueData: [number, number | null][] = [];
      let urineRawValueData: [number, number | null][] = [];
      let urineVolumeData: [number, number | null][] = [];
      let urineRawVolumeData: [number, number | null][] = [];

      if (showMedicalContent) {
        const visualized = (urineDataRes || []) as unknown as IGetVisualizedUrineVolumeRes;
        urineValueData = createVisualizedUrineVolumeData(visualized, 'value');
        urineRawValueData = createVisualizedUrineVolumeData(visualized, 'rawValue');
        urineVolumeData = createVisualizedUrineVolumeData(visualized, 'volume');
        urineRawVolumeData = createVisualizedUrineVolumeData(visualized, 'rawVolume');
      } else {
        const sensorReactions = urineDataRes as unknown as IGetSensorReactionRes;
        const resamples = (rawUrineRes || []) as unknown as IGetResamples;
        urineValueData = createSensorReactionData(sensorReactions);
        urineRawValueData = createResampleData(resamples);
        urineVolumeData = urineValueData;
        urineRawVolumeData = urineRawValueData;
      }

      setResident(resident);
      setOption(
        resident,
        urineValueData,
        urineRawValueData,
        urineVolumeData,
        urineRawVolumeData,
        notificationScatter,
        careRecordScatter,
        residualUrineData,
        activityStatusData
      );
    });
  }, [residentId, range, resident, isResampleView, isAbsoluteViewMode, isActivityStatusViewMode, showMedicalContent]);

  const handleUpdateLine = React.useCallback(
    async (data: IUpdateResidentParams) => {
      handleCloseLineChangeModal();
      try {
        await api.put('/residents/:residentId', { residentId, ...data });
        fetchCharts();
        showSnackbar(t('almostLineChanged', 'そろそろラインを変更しました'));
      } catch (e) {
        showSnackbar('エラーが発生しました', 'error');
      }
    },
    [fetchCharts, handleCloseLineChangeModal, residentId, showSnackbar, t]
  );

  const setOption = React.useCallback(
    (
      residentData: IGetResidentRes | undefined,
      urineValueData: [number, number | null][],
      urineRawValueData: [number, number | null][],
      urineVolumeData: [number, number | null][],
      urineRawVolumeData: [number, number | null][],
      notificationData: ScatterItem[],
      careRecordData: ScatterItem[],
      residualUrineData: ResidualUrineItem[],
      activityStatusData: number[][]
    ) => {
      const urineValueDataSource = cloneSeriesData(urineValueData);
      const urineRawValueDataSource = cloneSeriesData(urineRawValueData);
      const urineVolumeDataSource = cloneSeriesData(urineVolumeData);
      const urineRawVolumeDataSource = cloneSeriesData(urineRawVolumeData);
      const residualUrinePlotData = createResidualUrinePlotData(residualUrineData, isAbsoluteViewMode);
      const yAxisMax =
        showMedicalContent && isAbsoluteViewMode
          ? getMedicalAbsoluteYAxisMax(urineVolumeDataSource, urineRawVolumeDataSource, residualUrineData)
          : 10;
      const tickInterval = showMedicalContent && isAbsoluteViewMode ? yAxisMax / 5 : 2;
      const careRecordDataSource = alignScatterY(careRecordData, yAxisMax);

      const option: Highcharts.Options = {
        chart: {
          zoomType: 'x',
          height: 180,
        },
        time: {
          timezoneOffset: new Date().getTimezoneOffset(),
        },
        title: {
          text: '',
        },
        xAxis: {
          type: 'datetime',
          min: Date.parse(range.start.format()),
          max: Date.parse(range.end.format()),
          showEmpty: true,
          opposite: false,
          gridLineWidth: 1,
          lineWidth: 0,
          tickWidth: 0,
          minPadding: 0,
          maxPadding: 0,
          plotLines: createXAxisPlotLines(range.start, range.end, residualUrinePlotData),
          dateTimeLabelFormats: {
            minute: '%H:%M',
            hour: '%H:00',
            day: t('graphDayFormat', '%m月%d日'),
            week: t('graphDayFormat', '%m月%d日'),
            month: t('graphYearFormat', '%m月%d日'),
          },
          crosshair: {
            width: 2,
          },
        },
        yAxis: {
          title: {
            text: '',
          },
          labels: {
            formatter: function () {
              return yAxisLabelFormatter(this.value, isAbsoluteViewMode, showMedicalContent);
            },
          },
          plotLines: getYGrids(residentData?.bigBladderNotification, residentData?.bladderThresholdPercentage),
          max: yAxisMax,
          min: 0,
          tickInterval,
          crosshair: {
            width: 2,
          },
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          series: {
            marker: {
              enabled: false,
            },
          },
          scatter: {
            marker: {
              enabled: true,
            },
            states: {
              hover: {
                enabled: true,
              },
            },
          },
        },
        series: [
          {
            name: t('urineGraphLabel', '尿量'),
            // @ts-ignore
            data: cloneSeriesData(isAbsoluteViewMode ? urineVolumeDataSource : urineValueDataSource),
            yAxis: 0,
            color: '#7BD6E7',
            zIndex: 1,
            type: 'area',
            lineWidth: 3,
            fillOpacity: 0.5,
            tooltip: {
              headerFormat: '<b>{series.name}</b><br>',
              pointFormatter: function () {
                return tooltipFormatter(this.x, this.y, isAbsoluteViewMode, showMedicalContent);
              },
            },
          },
          {
            name: t('rawGraph', '補正前グラフ'),
            // @ts-ignore
            data: cloneSeriesData(isAbsoluteViewMode ? urineRawVolumeDataSource : urineRawValueDataSource),
            yAxis: 0,
            zIndex: 0,
            type: 'area',
            fillColor: {
              linearGradient: {
                x1: 0,
                x2: 0,
                y1: 0,
                y2: 1,
              },
              stops: [
                [0, 'rgba(105, 147, 209, 1)'],
                [1, 'rgba(187, 212, 251, 0.2)'],
              ],
            },
            lineWidth: 1,
            lineColor: '#C0C0C0',
            tooltip: {
              headerFormat: '<b>{series.name}</b><br>',
              pointFormatter: function () {
                return tooltipFormatter(this.x, this.y, isAbsoluteViewMode, showMedicalContent);
              },
            },
            visible: isResampleView,
          },
          {
            name: t('notification', '通知'),
            marker: {
              enabled: true,
              symbol: 'circle',
            },
            zIndex: 3,
            data: notificationData,
            type: 'line', // scatterだとエリア外のマウスオーバーでtooltipがでないためタイプをlineに
            lineWidth: 0,
            states: {
              hover: {
                lineWidth: 0,
                lineWidthPlus: 0,
              },
            },
            tooltip: {
              headerFormat: '<b>{series.name}</b><br>',
              pointFormat: t('graphDateTimePlotFormat', '{point.name}<br />{point.x:%m月%d日 %H時%M分}'),
            },
          },
          {
            name: t('careHistory', 'ケア履歴'),
            marker: {
              enabled: true,
              symbol: 'square',
            },
            zIndex: 3,
            data: careRecordDataSource,
            type: 'line', // scatterだとエリア外のマウスオーバーでtooltipがでないためタイプをlineに
            lineWidth: 0,
            states: {
              hover: {
                lineWidth: 0,
                lineWidthPlus: 0,
              },
            },
            tooltip: {
              headerFormat: '<b>{series.name}</b><br>',
              pointFormat: t('graphDateTimePlotFormat', '{point.name}<br />{point.x:%m月%d日 %H時%M分}'),
            },
          },
          {
            name: isAbsoluteViewMode
              ? t('residualUrineVolume', '残尿量')
              : t('UrineAccumulation', '尿のたまり具合'),
            data: residualUrinePlotData,
            color: '#2C8ED9',
            type: 'line',
            lineWidth: 0,
            states: {
              hover: {
                lineWidth: 0,
                lineWidthPlus: 0,
              },
            },
            marker: {
              enabled: true,
              symbol: 'diamond',
              radius: 6,
            },
            tooltip: {
              headerFormat: '<b>{series.name}</b><br>',
              pointFormat: t('graphDateTimePlotFormat', '{point.name}<br />{point.x:%m月%d日 %H時%M分}'),
            },
            zIndex: 4,
            stickyTracking: false,
            visible: showMedicalContent,
          },
          // 姿勢グラフとxAxisの目盛りを同期させるためにheatmapの空データを挿入
          {
            type: 'heatmap',
            data: [[0, 0, 0]],
            colsize: 8.5 * 36e3,
            dataLabels: { enabled: false },
          },
        ],
        credits: {
          enabled: false,
        },
      };

      const activityOption: Highcharts.Options = {
        title: {
          text: '',
        },
        chart: {
          zoomType: 'x',
          height: 60,
          marginTop: 0,
          marginLeft: isAbsoluteViewMode ? 63 : 40,
          marginRight: 8,
          shadow: false,
          animation: false,
          backgroundColor: '#FFFFFF',
        },
        time: {
          timezoneOffset: new Date().getTimezoneOffset(),
        },
        xAxis: {
          type: 'datetime',
          min: Date.parse(range.start.format()),
          max: Date.parse(range.end.format()),
          dateTimeLabelFormats: {
            minute: '%H:%M',
            hour: '%H:00',
            day: t('graphDayFormat', '%m月%d日'),
            week: t('graphDayFormat', '%m月%d日'),
            month: t('graphYearFormat', '%m月%d日'),
          },
        },
        yAxis: {
          categories: [''],
          reversed: false,
          gridLineWidth: 0,
          title: { text: '' },
        },
        colorAxis: {
          min: 0,
          max: 7,
          // stopsの値は色コードは姿勢コード÷姿勢の総数で求めた値を入れる
          stops: [
            [0, '#FFF4CC'],
            [0.125, '#FFF4CC'],
            [0.25, '#000000'],
            [0.375, '#141F80'],
            [0.5, '#D7F5FF'],
            [0.625, '#5855DB'],
            [0.75, '#4EC1D0'],
            [0.875, '#B092E2'],
            [1, '#FF7152'],
          ],
        },
        series: [
          {
            type: 'heatmap',
            boostThreshold: 100,
            data: activityStatusData,
            colsize: 8.5 * 36e3,
            rowsize: 1,
            index: 0,
            turboThreshold: Number.MAX_VALUE,
            dataLabels: { enabled: false },
            tooltip: {},
          },
        ],
        tooltip: {
          enabled: false,
        },
        legend: {
          enabled: false,
        },
        credits: {
          enabled: false,
        },
        exporting: {
          enabled: false,
        },
        boost: {
          useGPUTranslations: true,
        },
      };

      const nextChart = {
        id: uuid(),
        option,
        heatmap: activityOption,
        range,
        urineValueData: urineValueDataSource,
        urineRawValueData: urineRawValueDataSource,
        urineVolumeData: urineVolumeDataSource,
        urineRawVolumeData: urineRawVolumeDataSource,
        residualUrineData,
      };

      setCharts((prev) => {
        if (range.isCreate) return [...prev, nextChart];
        if (!prev[0]) return [nextChart];
        // 先頭チャートは id を維持しつつ中身だけ差し替える（不変更新）
        const updatedFirst = { ...prev[0], ...nextChart, id: prev[0].id };
        return [updatedFirst, ...prev.slice(1)];
      });
    },
    [
      createXAxisPlotLines,
      residentId,
      range,
      resident,
      isResampleView,
      isAbsoluteViewMode,
      isActivityStatusViewMode,
    ]
  );

  const handleChangeStart = React.useCallback((start) => {
    setRange((prev) => {
      return {
        ...prev,
        start: dayjs(start).local().utc(),
        isCreate: false,
      };
    });
  }, []);

  const handleChangeEnd = React.useCallback((end) => {
    setRange((prev) => {
      return {
        ...prev,
        end: dayjs(end).local().utc(),
        isCreate: false,
      };
    });
  }, []);

  const handleModalClose = React.useCallback(() => {
    setUrineVolumeCaptionOpen(false);
  }, [isUrineVolumeCaptionOpen]);

  React.useEffect(() => {
    if (!range) return;
    (async () => {
      await fetchDataset();
    })();
    fetchCharts();
  }, [range]);

  return (
    <div>
      <div css={GraphHeader}>
        <div css={ButtonContainer}>
          <LineChangeButton
            startIcon={<img src='../../../../images/icons/bell.svg' />}
            variant='contained'
            onClick={handleOpenLineChangeModal}
          >
            {t('changeAlmostThreshold', 'そろそろライン変更')}
          </LineChangeButton>
          <div css={ButtonRightElement}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isActivityStatusViewMode}
                  onChange={(v) => {
                    toggleActivityStatus(v.target.checked);
                  }}
                />
              }
              label={t('displayActivityTrend', '活動傾向表示')}
            />
            {/* Professionalは月額課金プランのみ表示、Medicalは常に表示 */}
            {(showMedicalContent || currentNursingHome?.membershipStatus === 'paid') && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAbsoluteViewMode}
                    onChange={(v) => {
                      toggleAbsoluteSensorReaction(v.target.checked);
                    }}
                  />
                }
                label={'尿量実測値表示'}
              />
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={isResampleView}
                  onChange={(v) => {
                    const checked = v.target.checked;
                    setResampleView(checked);
                    toggleResample(checked);
                  }}
                />
              }
              label={
                <span css={RawGraphLabel}>
                  {t('rawGraph', '補正前グラフ')}
                  <RawGraphHintTooltip size={22} />
                </span>
              }
            />
            {!showMedicalContent && (
              <OutlineButton onClick={handleOpenDrawer}>
                <img src='/images/residents/dataset.svg' />
                {t('dataset', 'データセット')}
              </OutlineButton>
            )}
            <IconButton onClick={() => setUrineVolumeCaptionOpen(true)}>
              <HelpOutline />
            </IconButton>
          </div>
        </div>
        <div css={CareRecordLabelRow}>
          <div css={CareRecordLabel}>
            <div css={ColorLine}></div>
            <span>{t('almostThreshold', 'そろそろライン')}</span>
          </div>
          <div css={CareRecordLabel}>
            <div css={ColorCircle} style={{ backgroundColor: color.notification_yellow }}></div>
            <span>{t('almostNotificiation', 'そろそろ通知')}</span>
          </div>
          <div css={CareRecordLabel}>
            <div css={ColorCircle} style={{ backgroundColor: color.notification_pink }}></div>
            <span>{t('checkNotificiation', 'でたかも通知')}</span>
          </div>
          <div css={CareRecordLabel}>
            <div css={ColorCircle} style={{ backgroundColor: color.notification_red }}></div>
            <span>
              {serviceT('gotUpNotificiation')}
            </span>
          </div>
          {showMedicalContent && (
            <div css={CareRecordLabel}>
              <div css={ColorDiamond}></div>
              <span>{t('residualUrineMeasurement', '残尿測定')}</span>
            </div>
          )}
        </div>
        <div css={CareRecordLabelRow}>
          <div css={CareRecordLabel}>
            <div css={ColorRect} style={{ backgroundColor: '#72FBFD' }}></div>
            <span>{serviceT('toiletYesDiaperOrPadNo')}</span>
          </div>
          <div css={CareRecordLabel}>
            <div css={ColorRect} style={{ backgroundColor: '#75F94C' }}></div>
            <span>{serviceT('toiletYesDiaperOrPadYes')}</span>
          </div>
          <div css={CareRecordLabel}>
            <div css={ColorRect} style={{ backgroundColor: '#CCCCCC' }}></div>
            <span>{serviceT('toiletNoDiaperOrPadYes')}</span>
          </div>
          <div css={CareRecordLabel}>
            <div css={ColorRect} style={{ backgroundColor: '#6D6D6D' }}></div>
            <span>{serviceT('toiletNoDiaperOrPadNo')}</span>
          </div>
        </div>
        <ActivityStatusGraphCaption />
      </div>

      <div css={GraphContainer}>
        <div css={DatePickerContainer}>
          <DateTimePicker
            ampm={false}
            views={['year', 'month', 'day', 'hours', 'minutes']}
            value={range.start.local()}
            maxDate={range.end.local()}
            onChange={handleChangeStart}
            renderInput={(params) => <TextField variant={'standard'} {...params} helperText={null} />}
          />
          〜
          <DateTimePicker
            ampm={false}
            views={['year', 'month', 'day', 'hours', 'minutes']}
            value={range.end.local()}
            minDate={range.start.local()}
            maxDate={dayjs().local().add(1, 'day').set('hour', 0).set('minute', 0).set('second', 0)}
            onChange={handleChangeEnd}
            renderInput={(params) => <TextField variant={'standard'} {...params} helperText={null} />}
          />
        </div>
        {charts.length > 0 && (
          <>
            {charts.map((chart, index) => (
              <DetailGraphItem
                key={chart.id}
                id={chart.id}
                graphOption={{ ...chart.option }}
                activityGraphOption={{ ...chart.heatmap }}
                range={chart.range}
                isFirst={index === 0}
                isResampleView={isResampleView}
                isActivityStatusView={isActivityStatusViewMode}
                onDelete={handleDeleteGraph}
              />
            ))}
          </>
        )}
      </div>
      {isLineChangeModalOpen && resident && (
        <ChangeLineModal isOpen resident={resident} onClose={handleCloseLineChangeModal} onSubmit={handleUpdateLine} />
      )}
      {isDrawerOpen && datasets && (
        <>
          {datasets.length > 0 && (
            <Drawer sx={{ position: 'relative' }} anchor='right' open={isDrawerOpen} onClose={handleCloseDrawer}>
              <Dataset
                datasets={datasets}
                addGraph={handleCreateChart}
                updateGraph={handleUpdateChart}
                toggleFavorite={handleUpdateFavorite}
                onClose={handleCloseDrawer}
              />
            </Drawer>
          )}
        </>
      )}
      {isUrineVolumeCaptionOpen && (
        <RecordDialog isOpen={isUrineVolumeCaptionOpen} dialogWidth={'620px'} onClose={handleModalClose}>
          <div css={[CaptionTitle, CaptionContainer]}>10段階表示と尿量値の目安</div>
          <div css={CaptionContainer}>
            <span css={CaptionText}>数値0： 100ml以下</span>
            <span css={CaptionText}>数値3： 120ml程度</span>
            <span css={CaptionText}>数値5： 200ml程度</span>
            <span css={CaptionText}>数値8： 320ml程度</span>
            <span css={CaptionText}>数値10：400ml程度</span>
          </div>
          <div css={CaptionContainer}>
            <span css={AnnotationText}>※ 個人により数値のばらつきがあるため、1つの目安としてご参照ください</span>
          </div>
          <MUIButton variant='contained' color='secondary' disableElevation onClick={handleModalClose}>
            {t('close', '閉じる')}
          </MUIButton>
        </RecordDialog>
      )}
    </div>
  );
};
